import { formatQuantity } from "@bl/domain";
import { Link } from "@tanstack/react-router";
import { createLucideIcon, UserRoundArrowLeft } from "lucide-react";
import { useState } from "react";
import { useBorrowing, useItems } from "../api";
import { Empty, ErrorNote, Toolbar, ToolbarButton, usePageTitle } from "../components/ui";
import { MovementDialog, type MovementMode } from "../forms/MovementDialog";

/**
 * Lucide's user-round-arrow-left with its arrow turned to point away, for stock going out (Tyler,
 * 2026-10-02). Lucide has no right-hand one.
 */
const UserRoundArrowRight = createLucideIcon("user-round-arrow-right", [
  ["path", { d: "m19 16 3 3", key: "arrow-tip" }],
  ["path", { d: "M2 21a8 8 0 0 1 12.664-6.5", key: "shoulders" }],
  ["path", { d: "M16 19h6l-3 3", key: "arrow" }],
  ["circle", { cx: "10", cy: "8", r: "5", key: "head" }],
]);
export function BorrowingPage() {
  usePageTitle("Borrowing");
  const { data: balances = [], isLoading, error } = useBorrowing();
  const { data: items = [] } = useItems();
  // A new loan names no item, so the dialog asks which (Scott's Jam clip, 2026-10-02).
  const [dialog, setDialog] = useState<{
    mode: MovementMode;
    itemId?: string;
    who?: string;
  } | null>(null);
  const unitOf = (id: string) => items.find((i) => i.id === id)?.displayUnit ?? "g";
  const nameOf = (id: string, fallback: string) =>
    items.find((i) => i.id === id)?.displayName ?? fallback;
  const borrow = () => setDialog({ mode: "borrow-in" });
  const give = () => setDialog({ mode: "lend-out" });
  return (
    <>
      <Toolbar
        left={
          <>
            <ToolbarButton
              icon={<UserRoundArrowLeft size={20} />}
              label="Borrow"
              title="Borrow from another brewery"
              onClick={borrow}
            />
            <ToolbarButton
              icon={<UserRoundArrowRight size={20} />}
              label="Give"
              title="Lend to another brewery"
              onClick={give}
            />
          </>
        }
      />
      <div className="page-body">
        <h1 className="page-title">Borrowing</h1>
        <p className="muted" style={{ marginTop: -8 }}>
          Stock borrowed from or lent to other breweries that has not been squared up yet.
        </p>
        <ErrorNote error={error} />
        {isLoading ? <p className="muted">Loading</p> : null}
        {!isLoading && balances.length === 0 ? (
          <Empty title="All square">
            <p>Nothing is borrowed or lent right now.</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <button type="button" className="button primary" onClick={borrow}>
                <UserRoundArrowLeft size={16} /> Borrow
              </button>
              <button type="button" className="button" onClick={give}>
                <UserRoundArrowRight size={16} /> Give
              </button>
            </div>
          </Empty>
        ) : null}
        {balances.length > 0 ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Brewery</th>
                  <th>Item</th>
                  <th className="num">We owe them</th>
                  <th className="num">They owe us</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {balances.map((b) => (
                  <tr key={`${b.counterparty}-${b.itemId}`}>
                    <td>{b.counterparty}</td>
                    <td>
                      <Link to="/items/$itemId" params={{ itemId: b.itemId }}>
                        {nameOf(b.itemId, b.itemName)}
                      </Link>
                    </td>
                    <td className="num">
                      {b.weOwe === "0" ? "" : formatQuantity(b.weOwe, { unit: unitOf(b.itemId) })}
                    </td>
                    <td className="num">
                      {b.theyOwe === "0"
                        ? ""
                        : formatQuantity(b.theyOwe, { unit: unitOf(b.itemId) })}
                    </td>
                    <td className="actions">
                      {b.weOwe !== "0" ? (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() =>
                            setDialog({
                              mode: "borrow-repay",
                              itemId: b.itemId,
                              who: b.counterparty,
                            })
                          }
                        >
                          Pay back
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="link-button"
                          onClick={() =>
                            setDialog({
                              mode: "lend-return",
                              itemId: b.itemId,
                              who: b.counterparty,
                            })
                          }
                        >
                          Got it back
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
      <MovementDialog
        open={dialog !== null}
        mode={dialog?.mode ?? "borrow-repay"}
        itemId={dialog?.itemId}
        counterparty={dialog?.who}
        onClose={() => setDialog(null)}
      />
    </>
  );
}
